/** Collection-only advertisement. Never has product IDs or the cards[] ref. */
(() => {
  if (window.ndCollectionAdLoaded) return;
  window.ndCollectionAdLoaded = true;
  const grids = new WeakSet();
  const initializedVideos = new WeakSet();
  const pendingVideos = new WeakSet();
  function playVideo(video) {
    if (document.visibilityState === 'hidden' || !video.isConnected || pendingVideos.has(video)) return;
    video.muted = true;
    video.defaultMuted = true;
    video.controls = false;
    video.loop = true;
    video.autoplay = true;
    video.playsInline = true;
    if (!video.paused && !video.ended) return;
    try {
      const result = video.play();
      if (result && typeof result.then === 'function') {
        pendingVideos.add(video);
        result.catch(() => {
          // Keep the uploaded cover visible when a WebView denies autoplay.
          video.parentElement?.removeAttribute('data-video-playing');
        }).finally(() => pendingVideos.delete(video));
      }
    } catch {
      video.parentElement?.removeAttribute('data-video-playing');
    }
  }
  function initializeVideos(root) {
    root.querySelectorAll('[data-nd-ad-video]').forEach(video => {
      if (!initializedVideos.has(video)) {
        initializedVideos.add(video);
        video.addEventListener('playing', () => video.parentElement?.setAttribute('data-video-playing', ''));
        ['error', 'emptied'].forEach(name => video.addEventListener(name, () => video.parentElement?.removeAttribute('data-video-playing')));
        ['loadedmetadata', 'canplay'].forEach(name => video.addEventListener(name, () => playVideo(video)));
        // Fallback if an older embedded browser does not honor the loop flag.
        video.addEventListener('ended', () => {
          try { video.currentTime = 0; } catch { /* Media may have been detached. */ }
          playVideo(video);
        });
      }
      if (!video.paused && !video.ended && video.readyState >= 2) {
        video.parentElement?.setAttribute('data-video-playing', '');
      }
      playVideo(video);
    });
  }
  function retryVideos() { initializeVideos(document); }
  // Retry synchronously within a user gesture or WeChat bridge ready event.
  ['WeixinJSBridgeReady', 'YixinJSBridgeReady'].forEach(name => document.addEventListener(name, retryVideos));
  ['touchend', 'click', 'keydown'].forEach(name => document.addEventListener(name, retryVideos, { passive: true }));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'hidden') retryVideos();
  });
  addEventListener('pageshow', retryVideos);
  let scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; update(); });
  }
  function update() {
    document.querySelectorAll('results-list').forEach(list => {
      const source = list.querySelector('template[data-nd-collection-ad]');
      const grid = list.querySelector('[ref="grid"]');
      if (!source || !grid) return;
      if (!grids.has(grid)) {
        grids.add(grid);
        if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(grid);
      }
      // Wait for pin deduplication and asynchronous product loading to finish.
      if (list.getAttribute('aria-busy') === 'true' || grid.querySelector('[data-top-product-skeleton]')) return;
      let ad = grid.querySelector(':scope > [data-nd-ad-card]');
      if (!ad) ad = source.content.firstElementChild.cloneNode(true);
      const cards = [...grid.children].filter(node => node.hasAttribute('data-product-id'));
      const tracks = getComputedStyle(grid).gridTemplateColumns;
      const columns = Math.max(1, (tracks.match(/(?:\d*\.)?\d+px/g) || []).length);
      const mobile = matchMedia('(max-width: 749px)').matches;
      const row = Math.max(1, Number(source.dataset[mobile ? 'mobileRow' : 'desktopRow']) || 1);
      const column = Math.min(columns, Math.max(1, Number(source.dataset[mobile ? 'mobileColumn' : 'desktopColumn']) || 1));
      const pinned = cards.filter(card => card.dataset.topProduct === 'true').length;
      const position = Math.min(cards.length, Math.max((row - 1) * columns + column - 1, pinned));
      const anchor = cards[position] || null;
      // No-op when already in position, so our MutationObserver cannot loop.
      if (ad.parentElement !== grid || (anchor ? ad.nextElementSibling !== anchor : ad !== grid.lastElementChild)) {
        grid.insertBefore(ad, anchor);
      }
      initializeVideos(ad);
      // Preserve the theme's indexed card classes after insertion and Load More.
      [...grid.children].filter(node => node.classList.contains('product-grid__item')).forEach((node, index) => {
        const nextClass = `product-grid__item--${index}`;
        if (node.classList.contains(nextClass)) return;
        [...node.classList].filter(name => /^product-grid__item--\d+$/.test(name)).forEach(name => node.classList.remove(name));
        node.classList.add(nextClass);
      });
    });
  }
  new MutationObserver(schedule).observe(document.documentElement, {
    subtree: true, childList: true, attributes: true,
    attributeFilter: ['aria-busy', 'product-grid-view', 'data-top-products-ready']
  });
  addEventListener('resize', schedule);
  addEventListener('popstate', schedule);
  document.addEventListener('shopify:section:load', schedule);
  schedule();
})();
